import React from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { PaziaButton, PaziaInput } from "components/atoms";
import { settingsApi } from "api";
import { useInvalidateSettings } from "api/hooks/useInvalidateSettings";
import { useToaster } from "utils";

export interface InvoiceSellerSettingsFormProps {
  name: string;
  address: string;
  nip: string;
  bankAccount: string;
  footerOwner: string;
  footerPhone: string;
  footerEmail: string;
  invoiceNumberSeries: string;
  logoUrl: string;
}

export const InvoiceSellerSettingsForm = (props: InvoiceSellerSettingsFormProps) => {
  const { showError, showSuccess } = useToaster();
  const invalidateSettings = useInvalidateSettings();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: props,
  });

  const onSubmit = async (values: InvoiceSellerSettingsValues) => {
    try {
      await settingsApi.saveInvoiceSeller(values);
      await invalidateSettings();
      showSuccess("Zapisano dane sprzedawcy na fakturze");
    } catch (error) {
      showError("Błąd podczas zapisywania danych sprzedawcy na fakturze");
    }
  };

  const {
    handleSubmit,
    formState: { isDirty, errors },
  } = methods;

  return (
    <FormProvider {...methods}>
      <form className="flex flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
        <h3 className="font-semibold">Dane sprzedawcy na fakturze PDF</h3>
        <PaziaInput label="Nazwa sprzedawcy" name="name" />
        <PaziaInput label="Adres sprzedawcy" name="address" />
        <PaziaInput label="NIP sprzedawcy" name="nip" />
        <PaziaInput label="Numer konta bankowego (z nazwą banku)" name="bankAccount" />
        <PaziaInput label="Stopka: imię i nazwisko" name="footerOwner" />
        <PaziaInput label="Stopka: telefon" name="footerPhone" />
        <PaziaInput label="Stopka: e-mail" name="footerEmail" />
        <PaziaInput
          label="Prefiks numeru faktury"
          name="invoiceNumberSeries"
          placeholder="np. PAZ"
          tooltip="Odróżnia faktury tego systemu, np. PAZ daje numery FV PAZ/1/10/2026 i FZal PAZ/1/10/2026. Zaczyna się od litery, tylko litery i cyfry, maksymalnie 10 znaków. Puste = numeracja bez prefiksu (FV 1/10/2026)."
        />
        <PaziaInput
          label="URL logo na fakturze"
          name="logoUrl"
          placeholder="https://..."
          tooltip="Absolutny adres https:// obrazka z logo. Puste = logo domyślne."
        />
        <div>
          <PaziaButton
            type="success"
            disabled={!isDirty}
            title="Zapisz"
            className={Object.keys(errors).length ? "mb-[26px]" : ""}
          />
        </div>
      </form>
    </FormProvider>
  );
};

const schema = yup
  .object({
    name: yup.string().required(),
    address: yup.string().required(),
    nip: yup.string().required(),
    bankAccount: yup.string().required(),
    footerOwner: yup.string().required(),
    footerPhone: yup.string().required(),
    footerEmail: yup.string().required(),
    // Sent as-is: "" clears the value (the API treats a missing/null field as "leave unchanged").
    invoiceNumberSeries: yup
      .string()
      .max(10, "Prefiks może mieć maksymalnie 10 znaków")
      .matches(/^([A-Za-z][A-Za-z0-9]*)?$/, "Prefiks musi zaczynać się od litery i zawierać tylko litery i cyfry"),
    logoUrl: yup.string().matches(/^(https:\/\/\S+)?$/, "Podaj absolutny adres URL zaczynający się od https://"),
  })
  .required();

export type InvoiceSellerSettingsValues = yup.InferType<typeof schema>;
